<template>
  <div>
    <div class="content-header">
      <div class="container-fluid">
        <div class="row mb-2">
          <div class="col-sm-6">
            <h1 class="m-0">{{ this.$route.name }}</h1>
          </div>
          <div class="col-sm-6">
            <ol class="breadcrumb float-sm-right">
              <li class="breadcrumb-item active">
                <i v-bind:class="routeIcon"></i> {{ $t(this.$route.name) }}
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>

    <section class="content">
      <div class="container-fluid">
        <div class="row col-lg-12">
          <div class="form-group col-sm-4">
            <EditForm
              :default-values="{}"
              title="Create dealer"
              @finish="handleItemsUpdate"
              create
            >
              <template #button="{ onclick }">
                <b-button type="button" variant="success" @click="onclick"
                  ><i class="fas fa-plus"></i> Create</b-button
                >
              </template>
            </EditForm>
          </div>
        </div>

        <b-row class="col-lg-12">
          <div
            class="col-lg-3 col-md-3 col-sm-12"
            v-for="{ id, label } in filterInputs"
            v-bind:key="id"
          >
            <b-form-group
              label-cols-lg="3"
              :label-for="id + '-filter-input'"
              :label="label"
            >
              <b-form-input
                :id="id + '-filter-input'"
                v-model="filters[id]"
                autocomplete="off"
                :placeholder="'Enter ' + label"
              />
            </b-form-group>
          </div>
        </b-row>

        <b-row class="col-lg-12">
          <div class="col-lg-12 col-md-12 col-sm-12 apply-clear">
            <FilterButtons
              :cancel="this.cancel"
              :loadFn="loadItems"
              :cancelFn="cancelRequest"
              :resetFn="clearFilters"
            />
          </div>
        </b-row>

        <div class="row col-12" id="loading-container">
          <div class="col-12 mb-10">
            <div class="mb-10">
              <div class="table-wrapper mb-10">
                <b-table
                  :striped="true"
                  :small="true"
                  :hover="true"
                  :empty-text="emptyText"
                  show-empty
                  :busy="!!cancel"
                  :items="items"
                  :fields="fields"
                  :sort-by="sortBy"
                  :sort-desc="sortDesc"
                  :no-local-sorting="true"
                  :sort-icon-left="true"
                  @sort-changed="sortingChanged"
                >
                  <template #cell(manager)="row">
                    {{ row.item.manager === 1 ? 'true' : '' }}
                  </template>
                  <template #cell(viewer)="row">
                    {{ row.item.viewer === 1 ? 'true' : '' }}
                  </template>
                  <template #cell(tables)="row">
                    <span
                      v-if="!row.item.tables || row.item.tables.length === 0"
                      class="text-muted"
                    >
                      —
                    </span>
                    <span
                      v-else-if="row.item.tables.length <= 3"
                      class="badge badge-info mr-1"
                      v-for="tableId in row.item.tables"
                      :key="tableId"
                    >
                      {{ tableId }}
                    </span>
                    <span v-else>
                      <span
                        class="badge badge-info mr-1"
                        v-for="tableId in row.item.tables.slice(0, 3)"
                        :key="tableId"
                      >
                        {{ tableId }}
                      </span>
                      <b-badge
                        variant="secondary"
                        v-b-tooltip.hover
                        :title="getAllTablesText(row.item.tables)"
                        style="cursor: help;"
                      >
                        +{{ row.item.tables.length - 3 }}
                      </b-badge>
                    </span>
                  </template>
                  <template #cell(photo)="row">
                    <b-img
                      v-if="row.item.photo"
                      :src="row.item.photo"
                      width="50"
                      height="50"
                      rounded
                      alt="Dealer photo"
                    ></b-img>
                  </template>
                  <template #cell(actions)="row">
                    <EditForm
                      :defaultValues="row.item"
                      title="Edit dealer"
                      @finish="handleItemsUpdate"
                    />
                    <RemoveForm
                      :object="row.item.name"
                      message="Please confirm removing dealer"
                      :url="'/api/v1/dealers/' + row.item.id"
                      @finish="handleItemsUpdate"
                    />
                  </template>
                </b-table>
              </div>
            </div>
            <div class="row mb-10">
              <div class="col-12">
                <b-pagination
                  class="float-right"
                  v-model="pagination.page"
                  :total-rows="pagination.records"
                  :per-page="pagination.onPage"
                  @change="onPageChange"
                  first-number
                  last-number
                />
              </div>
            </div>
            <div class="row mb-10">
              <div class="col-12">
                <ve-pagination
                  class="float-right"
                  :page-index="pagination.page"
                  :page-size="pagination.onPage"
                  :total="pagination.records"
                  :pagingCount="pagination.count"
                  :layout="['total', 'sizer']"
                  :pageSizeOption="pagination.pageSizeOption"
                  @on-page-size-change="onPageSizeChange"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import EditDealerForm from '../../components/Dealers/EditDealerForm.vue'
import RemoveForm from '../../components/remove-form/RemoveForm.vue'
import axios from 'axios'
import { getRouteIcon, ROUTE_DEALERS } from '../../utils/routerIcons'
import FilterButtons from '../../components/FilterButtons'

function getDefaultPagination() {
  return {
    onPage: 10,
    page: 1,
    records: 0,
    count: 0,
    pageSizeOption: [10, 25, 50, 100, 200]
  }
}

function getDefaultSort() {
  return {
    column: 'password',
    order: 'ASC'
  }
}

export default {
  components: {
    EditForm: EditDealerForm,
    RemoveForm,
    FilterButtons
  },
  data() {
    return {
      gameTypes: [],
      error: null,
      cancel: null,
      fields: [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'uid', label: 'Uid' },
        { key: 'shortName', label: 'Nick Name', sortable: true },
        { key: 'password', label: 'Password', sortable: true },
        { key: 'manager', label: 'Manager' },
        { key: 'viewer', label: 'Viewer' },
        { key: 'tables', label: 'Tables' },
        { key: 'authCode', label: 'Barcode' },
        { key: 'photo', label: 'Photo' },
        { key: 'actions', label: 'Actions' }
      ],
      items: [],
      sorts: getDefaultSort(),
      filters: {},
      pagination: getDefaultPagination(),
      filterInputs: [
        { label: 'Name', id: 'name' },
        { label: 'Nick name', id: 'shortName' },
        { label: 'Uid', id: 'uid' },
        { label: 'Password', id: 'password' }
      ],
      sortBy: null,
      sortDesc: null
    }
  },
  computed: {
    emptyText() {
      if (this.cancel) {
        return 'Loading items..'
      }

      if (this.error) {
        return this.error
      }

      return 'There are no records to show'
    },
    routeIcon: () => getRouteIcon(ROUTE_DEALERS)
  },
  methods: {
    sortingChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.pagination.page = 1

      this.loadItems()
    },
    clearFilters() {
      this.filters = {}
      this.sortingChanged({
        sortBy: null,
        sortDesc: null
      })
    },
    handleItemsUpdate() {
      this.loadItems()
    },
    loadItems: function() {
      this.items = []

      let self = this

      this.$http({
        url: '/api/v1/dealers',
        params: {
          ...this.filters,
          // pagination
          limit: this.pagination.onPage,
          offset: this.pagination.onPage * (this.pagination.page - 1),

          // sort params
          orderBy: this.sortBy,
          direction: this.sortDesc ? 'DESC' : 'ASC'
        },
        method: 'GET',
        cancelToken: new axios.CancelToken(c => {
          self.cancel = c
        })
      })
        .then(resp => {
          let {
            items,
            pagination: { limit, totalCount }
          } = resp.data

          this.items = items
          this.pagination.onPage = limit
          this.pagination.records = totalCount
        })
        .catch(err => {
          this.items = []
          this.error = err
        })
        .finally(() => {
          this.cancel = null
        })
    },
    cancelRequest() {
      if (this.cancel) {
        this.cancel('this -> cancel previous request')
      }
    },
    onPageChange(page) {
      this.pagination.page = page
      this.loadItems()
    },
    onPageSizeChange(pageSize) {
      this.pagination.page = 1
      this.pagination.onPage = pageSize
      this.loadItems()
    },
    onFinishEdit() {
      this.loadItems()
    },
    getAllTablesText(tables) {
      if (!tables || tables.length === 0) {
        return 'No tables'
      }
      return tables.join(', ')
    }
  },
  mounted() {
    this.loadItems()
  }
}
</script>
